import { AfterViewInit, ChangeDetectorRef, Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ProductionOrderSetupPhaseState, WorkCenterCachedService } from 'chronos-core-client';
import { TelemetryQueryDto, TelemetryService, TelemetryValueDto } from 'chronos-panda-client';
import moment from 'moment-mini';
import { Observable } from 'rxjs';
import { ChartType, LoadingNotificationService, CounterHelper, LogService, DisplayWindow } from 'chronos-shared';
import { notificationTopic } from '@app/shared/utils';
import { finalize } from 'rxjs/operators';
import { Quantity } from 'projects/chronos-core-client/src/public-api';
import { ApproveDsService, ProductionOrderDsService, DowntimeDsService } from '@app/core/data-services';
import { SignalType } from 'projects/chronos-shared/src/lib/machine-chart/machine-data';

export enum ChartMode {
  EditSetup,
  EditSetupApprove,
  SplitDowntime,
  EditDowntime
}

export interface IMachineChartDialogConfiguration {
  chartMode: ChartMode;

  // common configuration
  workCenterId: number;
  productionOrderId: number;
  runId?: number;
  startTime: Date;
  endTime?: Date;
  quantity: Quantity;

  // split downtime related configuration
  downTimeId?: number;

  // approval related configuration
  approvalReportLineId?: number;
  approvalReportDate?: string;
  waste?: Quantity;
  maculature?: Quantity;

  // edit downtime related configuration
  startCounter?: number;
  endCounter?: number;
  latestEndTime?: Date;
}

@Component({
  selector: 'app-machine-chart-dialog',
  templateUrl: './machine-chart-dialog.component.html',
  styleUrls: ['./machine-chart-dialog.component.scss']
})
export class MachineChartDialogComponent implements OnInit, AfterViewInit {

  public readonly LOADING_TOPIC = notificationTopic.modalEditSetup;
  public readonly LOADING_TOPIC_EDITDOWNTIME = notificationTopic.editDowntime;

  public ChartMode = ChartMode;
  public earliestSetupStartTime?: Date;
  public latestSetupEndTime?: Date;
  public latestEventTime: Date;
  public workCenterId: number;
  public startTime: Date;
  public endTime: Date;
  public allowRangeEditStart: boolean = true;
  public allowRangeEditEnd: boolean = true;
  public splitTime: Date;
  public formGroup: UntypedFormGroup;
  public editedExternalOrderId: string;
  public productionOrderId: number;
  public start$: Observable<Date>;
  public isDisplayChart = true;
  public chartName: string;
  public waste: Quantity = { value: 0, unitId: '' };
  public maculature: Quantity = { value: 0, unitId: '' };
  public quantity: Quantity = { value: 0, unitId: '' };
  public approvalReportLineId: number;
  public approvalReportDate: string;
  public runId: number;
  public previousQuantity: number;
  public earliestSetupStartCounter: number;
  public latestSetupEndCounter: number;
  public endCounter: number;
  public startCounter: number;
  public orderSetupPhaseState: ProductionOrderSetupPhaseState;
  public submitDisabled = false;
  public chartMode: ChartMode;
  public minFractionDigits: number = null;
  public maxFractionDigits: number = null;
  public unitId: string;
  public isChartIsInApproveEditSetup: boolean;

  private dialogStartedAt: Date;
  private displayWindow: DisplayWindow | null = null;
  private counterCoefficient: number;
  private initialQuantity: number;
  private initialStartTime: Date;
  private initialEndTime: Date;
  private initialStartCounter: number;
  private initialEndCounter: number;
  private downtimeId: number;
  private counterData: TelemetryValueDto[] | null = null;
  private readonly PANDA_DATA_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]';
  private startTimeSelectionLog = new Logger();
  private endTimeSelectionLog = new Logger();
  private splitTimeSelectionLog = new Logger();

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private formBuilder: UntypedFormBuilder,
    private telemetryService: TelemetryService,
    private workCenterCachedService: WorkCenterCachedService,
    private cd: ChangeDetectorRef,
    private productionOrderDsService: ProductionOrderDsService,
    private approveDsService: ApproveDsService,
    private downtimeDsService: DowntimeDsService,
    @Inject(LOCALE_ID) public locale: string
  ) {}

  public ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  public ngOnInit(): void {
    this.dialogStartedAt = new Date();
    this.isChartIsInApproveEditSetup = this.chartMode === ChartMode.EditSetupApprove;
    this.initializeFormGroup();
    this.parseConfigData();
    this.isSubmitDisabled();
  }

  private initializeFormGroup(): void {
    this.formGroup = this.formBuilder.group({
      startDate: [''],
      setupQuantity: [''],
      waste: [''],
      maculature: [''],
      splitDateTime: [''],
      endDate: ['']
    });
  }

  private getData(): IMachineChartDialogConfiguration {
    return this.config.data;
  }

  private parseConfigData(): void {
    const data = this.getData();
    if (data) {
      this.chartMode = data.chartMode;

      this.approvalReportLineId = data.approvalReportLineId;
      this.approvalReportDate = data.approvalReportDate;
      this.workCenterId = data.workCenterId;
      this.productionOrderId = data.productionOrderId;
      this.runId = data.runId;
      this.startTime = this.toUtc(data.startTime);
      this.endTime = data.endTime === null || data.endTime === undefined ? this.toUtc(new Date()) : this.toUtc(data.endTime);
      this.waste = { ...data.waste };
      this.maculature = { ...data.maculature };
      this.quantity = { ...data.quantity };
      this.initialQuantity = data.quantity?.value;
      this.initialStartTime = this.clearTimeToMinutes(this.startTime);
      this.initialEndTime = this.clearTimeToMinutes(this.endTime);
      this.initialStartCounter = data.startCounter;
      this.initialEndCounter = data.endCounter;
      this.splitTime = this.endTime;
      this.formGroup.controls.startDate.setValue(this.startTime);
      this.formGroup.controls.setupQuantity.setValue(this.quantity?.value);
      this.formGroup.controls.endDate.setValue(this.endTime);
      this.formGroup.controls.splitDateTime.setValue(this.endTime);
      this.formGroup.controls.waste.setValue(this.waste);
      this.formGroup.controls.maculature.setValue(this.maculature);
      this.chartName = ChartType.editSetup;
      this.previousQuantity = this.quantity?.value;

      this.counterCoefficient = CounterHelper.GetCounterCoefficient(this.quantity?.unitId);

      switch (this.chartMode) {
        case ChartMode.EditSetup:
          this.waste = { value: 0, unitId: this.quantity?.unitId };
          this.maculature = { value: 0, unitId: this.quantity?.unitId };
          // fall through to edit setup approve

        case ChartMode.EditSetupApprove:
          this.EnsureProductionOrderId();
          this.EnsureRunId();
          this.getSetupPhaseLimitAsync(this.productionOrderId);
          this.getProductionOrderInfoAsync();
          break;

        case ChartMode.SplitDowntime:
          this.earliestSetupStartTime = this.startTime;
          this.latestSetupEndTime = this.endTime;
          break;

        case ChartMode.EditDowntime:
          this.EnsureProductionOrderId();
          this.EnsureRunId();
          this.earliestSetupStartTime = this.startTime;
          this.latestSetupEndTime = moment.utc(data.latestEndTime).toDate();
          this.startCounter = data.startCounter;
          this.endCounter = data.endCounter;
          this.getProductionOrderInfoAsync();
          break;
        default:
          throw new Error(`Invalid chart mode ${this.chartMode}`);
      }

      if (this.chartMode === ChartMode.EditDowntime || this.chartMode === ChartMode.SplitDowntime) {
        this.commonFieldsForDowntime(data);
      }

      this.startTimeSelectionLog.append(`Initial Start Time selected: ${this.startTime.toISOString()}, Counter: ${this.startCounter}`);
      this.endTimeSelectionLog.append(`Initial End Time selected: ${this.endTime.toISOString()}, Counter: ${this.endCounter}`);
      this.splitTimeSelectionLog.append(`Initial Split Time selected: ${this.splitTime.toISOString()}`);

      console.info(this.startTimeSelectionLog.getLogs());
      console.info(this.endTimeSelectionLog.getLogs());
      console.info(this.splitTimeSelectionLog.getLogs());
    }
  }

  public loadSetupQuantityDataAsync(reload: boolean = false): void {
    // the display window was not yet set, we cannot load the data
    if (!this.displayWindow) {
      return;
    }

    // we already have the data
    if (this.counterData && !(reload === true)) {
      return;
    }

    this.workCenterCachedService.getWorkCenter(this.workCenterId)
      .subscribe((workCenterDetails) => {
        if (workCenterDetails) {
          this.unitId = workCenterDetails.counterUnitId;

          const counterTelemetryQuery: TelemetryQueryDto = {
            wellKnownSignal: SignalType.counter_qty,
            resource: workCenterDetails.externalWorkCenterId,
            from: moment.utc(this.displayWindow.start).format(this.PANDA_DATA_FORMAT),
            to: moment.utc(this.displayWindow.end).format(this.PANDA_DATA_FORMAT),
            aggFunction: '',
            groupBy: '',
            resultPoints: ''
          };
          this.telemetryService.apiTelemetryPost({ body: counterTelemetryQuery })
            .subscribe((result) => {
              result.telemetryData.forEach((telemetryValue) => { telemetryValue.value = telemetryValue.value * this.counterCoefficient; });

              this.counterData = result.telemetryData;

              if (this.counterData && this.counterData.length > 0) {
                const latestTelemetryValue = this.counterData[this.counterData.length - 1];

                // we limit the editing range to the latest telemetry value - edits past this point would be invalid as it creates an incorrect time / counter relationship
                if (moment.utc(latestTelemetryValue.timestamp).isBefore(moment.utc(this.latestSetupEndTime))) {
                  console.warn('Latest telemetry value is before latest setup end time, adjusting latest setup end time and counter accordingly');
                  this.endTimeSelectionLog.append('Latest telemetry value is before latest setup end time, adjusting latest setup end time and counter accordingly');

                  this.latestEventTime = moment.utc(latestTelemetryValue.timestamp).toDate();
                  this.latestSetupEndTime = moment.utc(latestTelemetryValue.timestamp).toDate();
                  this.latestSetupEndCounter = latestTelemetryValue.value;
                }
              }
              else
              {
                console.warn('No telemetry data available for setup quantity');
                this.startTimeSelectionLog.append('No telemetry data available for setup quantity');
                this.endTimeSelectionLog.append('No telemetry data available for setup quantity');
              }
            });
        }
      });
  }

  public onDecline(): void {
    this.ref.close(false);
  }

  public onSubmit(): void {
    LoadingNotificationService.publish(this.LOADING_TOPIC, true);

    switch (this.chartMode) {
      case ChartMode.EditSetup:
        this.editSetupAsync();
        break;
      case ChartMode.EditSetupApprove:
        this.editSetupApprovalAsync();
        break;
      case ChartMode.SplitDowntime:
        this.updateSplitDownTimeAsync();
        break;
      case ChartMode.EditDowntime:
        this.updateEditDowntimeAsync();
        break;
      default:
        throw new Error(`Invalid chart mode ${this.chartMode}`);
    }
  }

  public clearTimeToMinutes(date: Date): Date {
    return moment(date).seconds(0).milliseconds(0).toDate();
  }

  private toUtc(time: Date): Date {
    return moment.utc(time).toDate();
  }

  public getProductionOrderInfoAsync(): void {
    if (!this.productionOrderId || this.productionOrderId === 0) {
      this.editedExternalOrderId = null;
      this.productionOrderId = null;
      this.runId = null;
      return;
    }

    this.productionOrderDsService.getProductionOrder(this.productionOrderId)
      .subscribe((productionOrder) => {
        if (productionOrder) {
          this.editedExternalOrderId = productionOrder.externalProductionOrderId;
        }
      });
  }

  public getSetupPhaseLimitAsync(productionOrderId: number): void {
    if (!productionOrderId || productionOrderId === 0) {
      return;
    }

    const calledFromApproveReport = !!this.approvalReportDate;

    this.productionOrderDsService
      .getSetupPhaseState(productionOrderId, this.runId, calledFromApproveReport)
      .subscribe((setupPhaseState) => {
        this.counterCoefficient = CounterHelper.GetCounterCoefficient(setupPhaseState.setupPhaseQuantity.unitId);

        // Note: the commented lines below are passed in to the dialog from the caller, this is especially important for approve
        //       in approve the caller passes in the setup start and end time based on the limits of the approve day
        //       in the future the view should be adjusted to return the correct data
        this.editedExternalOrderId = setupPhaseState.externalProductionOrderId;
        this.earliestSetupStartCounter = setupPhaseState.earliestSetupStartCounter;
        this.earliestSetupStartTime = setupPhaseState.earliestSetupStartTime ? new Date(setupPhaseState.earliestSetupStartTime) : null;
        this.latestSetupEndTime = setupPhaseState.latestSetupEndTime ? new Date(setupPhaseState.latestSetupEndTime) : null;
        this.latestSetupEndCounter = setupPhaseState.latestSetupEndCounter;
        this.latestEventTime = new Date(setupPhaseState.latestEventTime);
        this.startCounter = this.chartMode === ChartMode.EditDowntime ? this.config.data?.startCounter : setupPhaseState.setupStartCounter;
        this.endCounter = this.chartMode === ChartMode.EditDowntime ? this.config.data?.endCounter : setupPhaseState.setupEndCounter;

        this.allowRangeEditStart = this.isTimeAfterEarliestSetupStartTime(this.startTime, this.earliestSetupStartTime);
        this.allowRangeEditEnd = this.isTimeAfterEarliestSetupStartTime(this.endTime, this.earliestSetupStartTime);

        this.startTimeSelectionLog.append(`Initial Start Time selected: ${this.startTime.toISOString()}, Counter: ${this.startCounter}`);
        this.endTimeSelectionLog.append(`Initial End Time selected: ${this.endTime.toISOString()}, Counter: ${this.endCounter}`);
        this.splitTimeSelectionLog.append(`Initial Split Time selected: ${this.splitTime.toISOString()}`);

        console.info(this.startTimeSelectionLog.getLogs());
        console.info(this.endTimeSelectionLog.getLogs());
        console.info(this.splitTimeSelectionLog.getLogs());

        this.orderSetupPhaseState = setupPhaseState;

        this.loadSetupQuantityDataAsync();
      });
  }

  public editSetupAsync(): void {
    this.productionOrderDsService
      .editProductionOrderSetupPhase(
        moment.utc(this.startTime).toISOString(),
        moment.utc(this.endTime).toISOString(),
        this.quantity,
        this.startCounter,
        this.endCounter,
        this.productionOrderId,
        this.dialogStartedAt,
        this.startTimeSelectionLog.getLogs(),
        this.endTimeSelectionLog.getLogs()
      )
      .pipe(
        finalize(() => {
          LoadingNotificationService.publish(this.LOADING_TOPIC, false);
        })
      )
      .subscribe({
        complete: () => {
          LogService.success('SUCCESS_MESSAGE.EDIT_SETUP');
          this.ref.close(true);
        }
      });
  }

  public updateSplitDownTimeAsync(): void {
    this.downtimeDsService
      .splitDowntime(
        moment.utc(this.splitTime).toISOString(),
        this.downtimeId,
        this.workCenterId,
        this.splitTimeSelectionLog.getLogs()
      )
      .pipe(
        finalize(() => {
          LoadingNotificationService.publish(this.LOADING_TOPIC, false);
        })
      )
      .subscribe({
        complete: () => {
          LogService.success('SUCCESS_MESSAGE.SPLIT_DOWNTIME');
          this.ref.close(true);
        }
      });
  }

  public editSetupApprovalAsync(): void {
    this.approveDsService
      .editApprovalReportSetupPhase(
        moment.utc(this.startTime).toISOString(),
        moment.utc(this.endTime).toISOString(),
        this.quantity,
        this.waste,
        this.maculature,
        this.startCounter,
        this.endCounter,
        this.approvalReportLineId,
        this.approvalReportDate,
        this.workCenterId,
        this.productionOrderId,
        this.dialogStartedAt,
        this.startTimeSelectionLog.getLogs(),
        this.endTimeSelectionLog.getLogs()
      )
      .pipe(
        finalize(() => {
          LoadingNotificationService.publish(this.LOADING_TOPIC, false);
        })
      )
      .subscribe({
        complete: () => {
          LogService.success('SUCCESS_MESSAGE.EDIT_SETUP_APPROVAL');
          this.ref.close(true);
        }
      });
  }

  public onWasteChanged(): void {
    if (this.quantity.value === 0) {
      this.waste.value = 0;
      this.maculature.value = 0;
    }
    if (this.waste.value > this.quantity.value) {
      this.waste = this.quantity;
      this.maculature.value = 0;
    } else if (this.waste.value < 0) {
      this.waste.value = 0;
      this.maculature = this.quantity;
    } else {
      this.maculature.value = this.quantity.value - this.waste.value;
    }
    this.formGroup.controls.maculature.setValue(this.maculature.value);
    this.formGroup.controls.waste.setValue(this.waste.value);
  }

  public onMaculatureChanged(): void {
    if (this.quantity.value === 0) {
      this.waste.value = 0;
      this.maculature.value = 0;
    }
    if (this.maculature.value > this.quantity.value) {
      this.maculature = this.quantity;
      this.waste.value = 0;
    } else if (this.maculature.value < 0) {
      this.maculature.value = 0;
      this.waste = this.quantity;
    } else {
      this.waste.value = this.quantity.value - this.maculature.value;
    }
    this.formGroup.controls.maculature.setValue(this.maculature.value);
    this.formGroup.controls.waste.setValue(this.waste.value);
  }

  public setupQuantityChanged(): void {
    if (!this.counterData || this.counterData.length === 0) {
      console.warn('No setup quantity data available');
      return;
    }

    const setupQuantityEnter = this.quantity;
    this.endTimeSelectionLog.clear();

    for (let j = this.counterData.length - 1; j >= 0; j--) {
      const quantityCurrentTelemetry = this.getQuantityFromCounterValues(this.counterData[j].value, this.startCounter);

      if (quantityCurrentTelemetry === setupQuantityEnter.value) {
        // quantity derived from the telemetry value matches the target quantity exactly
        this.endTime = new Date(this.counterData[j].timestamp);
        this.endCounter = this.counterData[j].value;
        this.endTimeSelectionLog.append(
          `SetupQuantityChanged: EndTime: ${this.endTime.toISOString()}, Counter: ${this.endCounter}; ` +
          'direct match'
        );
        break;
      } else if (quantityCurrentTelemetry < setupQuantityEnter.value) {
        if (j !== this.counterData.length - 1) {
          // interpolate between two telemetry values
          const priorPoint = this.counterData[j];
          const nextPoint = this.counterData[j + 1];
          const priorQuantity = this.getQuantityFromCounterValues(priorPoint.value, this.startCounter);
          const nextQuantity = this.getQuantityFromCounterValues(nextPoint.value, this.startCounter);
          const factor = (setupQuantityEnter.value - priorQuantity) / (nextQuantity - priorQuantity);
          const priorTimeInMs = new Date(priorPoint.timestamp).getTime();
          const nextTimeInMs = new Date(nextPoint.timestamp).getTime();
          const searchTimeInMs = priorTimeInMs + (nextTimeInMs - priorTimeInMs) * factor;
          this.endTime = new Date(searchTimeInMs);
          this.endCounter = this.getEndCounterValueFromQuantity(this.startCounter, setupQuantityEnter.value);
          this.endTimeSelectionLog.append(
            `SetupQuantityChanged: EndTime: ${this.endTime.toISOString()}, Counter: ${this.endCounter}; ` +
            `interpolated between ${priorPoint.timestamp} (${priorPoint.value}) and ${nextPoint.timestamp} (${nextPoint.value})`
          );
        } else {
          // entered quantity is greater than available quantity, use the last telemetry point to set the maximum quantity
          this.endTime = new Date(this.counterData[j].timestamp);
          this.endCounter = this.counterData[j].value;
          this.quantity.value = this.getQuantityFromCounterValues(this.endCounter, this.startCounter);
          this.endTimeSelectionLog.append(
            `SetupQuantityChanged: EndTime: ${this.endTime.toISOString()}, Counter: ${this.endCounter}; ` +
            'direct match'
          );
        }
        break;
      }
    }

    if (this.isEndTimeBeforeStartTime(this.startTime, this.endTime) || this.endCounter < this.startCounter) {
      this.endTime = this.startTime;
      this.endCounter = this.startCounter;
      this.endTimeSelectionLog.append(`; clamped to start time: Time: ${this.endTime.toISOString()}, Counter: ${this.endCounter}`);
    }

    this.formGroup.controls.setupQuantity.setValue(this.quantity.value);
    if (this.quantity.value === 0) {
      this.waste.value = 0;
      this.maculature.value = 0;
    } else {
      this.waste.value = this.quantity.value - this.maculature.value;
    }
    if (this.waste.value < 0) {
      this.waste.value = 0;
    }
    if (this.maculature.value > this.quantity.value) {
      this.maculature.value = this.quantity.value - this.waste.value;
    }

    console.info(this.endTimeSelectionLog.getLogs());
    console.info(
      `Edit setup: NewSetupQuantity='${JSON.stringify(this.quantity)}',
      NewWaste='${this.formGroup.controls.waste.value}',
      NewMaculature='${this.formGroup.controls.maculature.value}'`
    );
    this.formGroup.controls.waste.setValue(this.waste.value);
  }

  public startTimeChanged(timeStamp: Date, onlyDay: boolean, onlyMinutes: boolean): void {
    if (this.earliestSetupStartTime !== null &&
        !this.isTimeAfterEarliestSetupStartTime(this.startTime, this.earliestSetupStartTime)) {
      console.info('Original start time is before earliest limit, no movement is allowed, ignoring change');

      return;
    }

    if (onlyDay) {
      const selectedDay = timeStamp.getDate();
      const selectedMonth = timeStamp.getMonth();
      const selectedYear = timeStamp.getFullYear();

      this.startTime.setDate(selectedDay);
      this.startTime.setMonth(selectedMonth);
      this.startTime.setFullYear(selectedYear);
      this.startTime = new Date(this.startTime);
    } else {
      this.startTime = timeStamp;
    }

    this.quantity.value = this.initialQuantity;

    if (onlyMinutes) {
      this.startTime = this.clearTimeToMinutes(this.startTime);
    }

    this.startTimeSelectionLog.clear();
    this.startTimeSelectionLog.append(`Start Time changed to: ${this.startTime.toISOString()}`);

    // Handle start time changes
    // Check if the start time is before the earliest limit
    let wasClamped = false;
    if (this.earliestSetupStartTime != null) {
      if (this.isTimeBeforeEarliestSetupStartTime(this.startTime, this.earliestSetupStartTime)) {
        this.startTime = moment(this.earliestSetupStartTime).toDate();
        this.startCounter = this.earliestSetupStartCounter;
        wasClamped = true;
        this.startTimeSelectionLog.append(`; clamped to earliest limit: ${this.earliestSetupStartTime.toISOString()}`);
      }
    }

    // Check if the start time is after the end time
    if (this.isStartTimeNotAfterEndTime(this.startTime, this.endTime)) {
      this.startTime = moment(this.endTime).toDate();
      this.startCounter = this.endCounter;
      wasClamped = true;
      this.startTimeSelectionLog.append(`; clamped to end time: ${this.endTime.toISOString()}`);
    }

    // limit movement based on display window
    if (this.startTime < moment(this.displayWindow.start).toDate()) {
      this.startTime = moment(this.displayWindow.start).toDate();
      this.startTimeSelectionLog.append(`; clamped to display window start time: ${this.displayWindow.start}`);
    }

    // determine the start counter
    if (!wasClamped) {
      this.startCounter = this.calculateCounter(this.startTime, this.startTimeSelectionLog);
    }

    this.startTimeSelectionLog.append(`; Counter: ${this.startCounter}`);

    this.calculateSetupQuantity();

    console.info(this.startTimeSelectionLog.getLogs());
  }

  public endTimeChanged(timeStamp: Date, onlyDay: boolean, onlyMinutes: boolean): void {
    if (this.earliestSetupStartTime !== null &&
        !this.isTimeAfterEarliestSetupStartTime(this.endTime, this.earliestSetupStartTime)) {
      console.info('Original end time is before earliest limit, no movement is allowed, ignoring change');

      return;
    }

    if (onlyDay && this.endTime) {
      const selectedDay = timeStamp.getDate();
      const selectedMonth = timeStamp.getMonth();
      const selectedYear = timeStamp.getFullYear();

      this.endTime.setDate(selectedDay);
      this.endTime.setMonth(selectedMonth);
      this.endTime.setFullYear(selectedYear);
      this.endTime = new Date(this.endTime);
    } else {
      this.endTime = timeStamp;
    }

    this.quantity.value = this.initialQuantity;

    if (onlyMinutes) {
      this.endTime = this.clearTimeToMinutes(this.endTime);
    }

    this.endTimeSelectionLog.clear();
    this.endTimeSelectionLog.append(`End Time changed to: ${this.endTime.toISOString()}`);

    // Handle end time changes
    // Check if the end time is after the latest limit
    let wasClamped = false;
    if (this.latestSetupEndTime != null) {
      if (this.isEndTimeAfterLatestSetupEndTime(this.endTime)) {
        this.endTime = moment(this.latestSetupEndTime).toDate();
        this.endCounter = this.latestSetupEndCounter;
        wasClamped = true;
        this.endTimeSelectionLog.append(`; clamped to latest limit: ${this.latestSetupEndTime.toISOString()}`);
      }
    }

    // Check if the end time is before the start time
    if (this.isEndTimeBeforeStartTime(this.startTime, this.endTime)) {
      this.endTime = moment(this.startTime).toDate();
      this.endCounter = this.startCounter;
      wasClamped = true;
      this.endTimeSelectionLog.append(`; clamped to start time: ${this.startTime.toISOString()}`);
    }

    // limit movement based on display window
    if (this.endTime > moment(this.displayWindow.end).toDate()) {
      this.endTime = moment(this.displayWindow.end).toDate();
      this.endTimeSelectionLog.append(`; clamped to display window end time: ${this.displayWindow.end}`);
    }

    // determine the end counter
    if (!wasClamped) {
      this.endCounter = this.calculateCounter(this.endTime, this.endTimeSelectionLog);
    }

    this.endTimeSelectionLog.append(`; Counter: ${this.endCounter}`);

    this.calculateSetupQuantity();

    console.info(this.endTimeSelectionLog.getLogs());
  }

  public splitTimeChanged(timeStamp: Date, onlyDay: boolean): void {
    if (onlyDay) {
      const selectedDay = timeStamp.getDate();
      this.splitTime.setDate(selectedDay);
      this.splitTime = new Date(this.splitTime);
    } else {
      this.splitTime = timeStamp;
    }

    this.splitTimeSelectionLog.clear();
    this.splitTimeSelectionLog.append(`Split Time changed to: ${this.splitTime.toISOString()}`);

    if (this.isEndTimeBeforeStartTime(this.startTime, this.splitTime)) {
      this.splitTime = this.startTime;
      this.splitTimeSelectionLog.append(`; clamped to start time: ${this.startTime.toISOString()}`);
    }

    if (this.isSplitTimeAfterEndTime(this.splitTime, this.endTime)) {
      this.splitTime = this.endTime;
      this.splitTimeSelectionLog.append(`; clamped to end time: ${this.endTime.toISOString()}`);
    }

    // lets calculate the corresponding counter value for the split time so we can compare it to the counter the backend will calculate
    const splitCounter = this.calculateCounter(this.splitTime, this.splitTimeSelectionLog);
    this.splitTimeSelectionLog.append(`; Counter: ${splitCounter}`);

    console.info(this.splitTimeSelectionLog.getLogs());

    this.formGroup.controls.splitDateTime.setValue(this.splitTime);
  }

  private getQuantityValue(): void {
    if (this.startTime === this.endTime) {
      this.quantity.value = 0;
      return;
    }

    if (this.endCounter >= 0 && this.startCounter >= 0 && this.counterCoefficient > 0) {
      this.quantity.value = this.getQuantityFromCounterValues(this.endCounter, this.startCounter);
      return;
    }
  }

  private getQuantityFromCounterValues(counterValue1: number, counterValue2: number): number {
    if (this.counterCoefficient) {
      return CounterHelper.roundToPrecision((counterValue1 - counterValue2) / this.counterCoefficient, this.unitId);
    }
    return Number.NaN;
  }

  private getEndCounterValueFromQuantity(startCounterValue: number, quantity: number): number {
    if (this.counterCoefficient) {
      return CounterHelper.roundToPrecision((quantity * this.counterCoefficient) + startCounterValue, this.unitId);
    }
    return Number.NaN;
  }

  private interpolateValues(firstValue: TelemetryValueDto, secondValue: TelemetryValueDto, time: string): number {
    const firstTime = moment.utc(firstValue.timestamp).toDate();
    const secondTime = moment.utc(secondValue.timestamp).toDate();

    if (firstTime === secondTime) {
      return firstValue.value;
    }

    const factor = (moment.utc(time).toDate().getTime() - firstTime.getTime()) / (secondTime.getTime() - firstTime.getTime());
    return Math.floor(firstValue.value + (secondValue.value - firstValue.value) * factor);
  }

  private calculateCounter(timeValue: Date, logger: Logger): number | null {
    if (this.counterData === null || this.counterData.length === 0) {
      console.warn('No setup quantity data available');
      logger.append('; No setup quantity data available');
      return null;
    }

    const time = moment.utc(timeValue).toISOString();
    for (let j = 0; j < this.counterData.length; j++) {
      if (this.counterData[j].timestamp >= time) {
        if (j === 0 || this.counterData[j].timestamp === time) {
          logger.append(`; Direct match at index ${j}, timestamp: ${JSON.stringify(this.counterData[j])}`);
          return this.counterData[j].value;
        } else if (this.counterData[j - 1].timestamp === time) {
          logger.append(`; Direct match at index ${j - 1}, timestamp: ${JSON.stringify(this.counterData[j - 1])}`);
          return this.counterData[j - 1].value;
        } else {
          logger.append(
            `; Interpolated between index ${j - 1} (${JSON.stringify(this.counterData[j - 1])}) ` +
            `and index ${j} (${JSON.stringify(this.counterData[j])})`
          );
          return this.interpolateValues(this.counterData[j - 1], this.counterData[j], time);
        }
      }
    }

    // if the time is after the last point, return the last value
    logger.append(
      `Time after last data point, using last value at index ${this.counterData.length - 1}: ` +
      `${JSON.stringify(this.counterData[this.counterData.length - 1])}`);
    return this.counterData[this.counterData.length - 1].value;
  }

  public calculateSetupQuantity(): void {
    console.info(
      `Edit setup: OriginalSetupQuantity='${this.formGroup.controls.setupQuantity.value}'
       OriginalWaste='${this.formGroup.controls.waste.value}',
       OriginalMaculature='${this.formGroup.controls.maculature.value}'`
    );
    this.getQuantityValue();

    this.quantity.value = Math.max(0, this.quantity.value);
    this.formGroup.controls.setupQuantity.setValue(this.quantity.value);

    if (this.quantity.value === 0) {
      this.waste.value = 0;
      this.maculature.value = 0;
    } else {
      this.waste.value = Math.max(0, this.quantity.value - this.maculature.value);
    }

    if (this.maculature.value > this.quantity.value) {
      this.maculature.value = this.quantity.value - this.waste.value;
    }

    this.formGroup.controls.waste.setValue(this.waste.value);
    this.formGroup.controls.maculature.setValue(this.maculature.value);

    console.info(
      `Edit setup: NewSetupQuantity='${JSON.stringify(this.quantity)}',
      NewWaste='${this.formGroup.controls.waste.value}',
      NewMaculature='${this.formGroup.controls.maculature.value}'`
    );
  }

  // reset the form
  public editDowntimeResetAsync(): void {
    LoadingNotificationService.publish(this.LOADING_TOPIC_EDITDOWNTIME, true);
    this.downtimeDsService
      .revertEditDowntime(this.downtimeId, this.workCenterId)
      .pipe(
        finalize(() => {
          LoadingNotificationService.publish(this.LOADING_TOPIC_EDITDOWNTIME, false);
          this.ref.close(true);
        })
      )
      .subscribe(
        () => {
          this.startTime = this.initialStartTime;
          this.endTime = this.initialEndTime;
          this.quantity.value = this.initialQuantity;
          this.startCounter = this.initialStartCounter;
          this.endCounter = this.initialEndCounter;
          this.formGroup.controls.endDate.setValue(this.endTime);
          this.formGroup.controls.setupQuantity.setValue(this.quantity.value);
          console.info('reset edit downtime in machine chart dialog');
        },
        (error) => {
          console.error('reset edit downtime in machine chart dialog failed', error);
        }
      );
  }

  // update edit downtime
  public updateEditDowntimeAsync(): void {
    this.downtimeDsService
      .editDowntime(
        this.downtimeId,
        this.workCenterId,
        moment.utc(this.startTime).toISOString(),
        this.startCounter,
        moment.utc(this.endTime).toISOString(),
        this.endCounter,
        this.quantity,
        this.endTimeSelectionLog.getLogs()
      )
      .pipe(
        finalize(() => {
          LoadingNotificationService.publish(this.LOADING_TOPIC, false);
        })
      )
      .subscribe({
        complete: () => {
          LogService.success('SUCCESS_MESSAGE.EDIT_DOWNTIME');
          this.ref.close(true);
          console.info('edit downtime post success');
        }
      });
  }

  private commonFieldsForDowntime(data: IMachineChartDialogConfiguration) {
    this.downtimeId = data.downTimeId;

    this.isDisplayChart = this.isChartDisplayed();

    switch (this.chartMode) {
      case ChartMode.SplitDowntime:
        this.chartName = ChartType.splitDowntime;
        break;
      case ChartMode.EditDowntime:
        this.chartName = ChartType.editDowntime;
        break;
      default:
        this.chartName = ChartType.editSetup;
    }
  }

  private isChartDisplayed(): boolean {
    switch (this.chartMode) {
      case ChartMode.EditSetup:
        return true;
      case ChartMode.EditSetupApprove:
        return true;
      case ChartMode.SplitDowntime:
        if (this.endTime.getTime() === new Date(0).getTime()) {
          // TODO: how to check for null end time?
          return true;
        }

        return (this.endTime.getTime() - this.startTime.getTime()) / (60 * 1000) >= 5;
      case ChartMode.EditDowntime:
        return true;
    }

    return false;
  }

  private isStartTimeNotAfterEndTime = (start: Date, end: Date) => (start && end ? start >= end : true);

  private isEndTimeBeforeStartTime = (start: Date, end: Date) => (start && end ? end <= start : true);

  private isSplitTimeAfterEndTime = (splitTime: Date, endtime: Date) => (splitTime && endtime ? splitTime >= endtime : true);

  private isTimeBeforeEarliestSetupStartTime = (start: Date, earliestSetupStartTime?: Date) =>
    (start && earliestSetupStartTime ? start <= earliestSetupStartTime : true);

  private isTimeAfterEarliestSetupStartTime = (start: Date, earliestSetupStartTime?: Date) =>
    (start && earliestSetupStartTime ? start >= earliestSetupStartTime : true);

  private isEndTimeAfterLatestSetupEndTime = (end: Date) =>
    (end && this.latestSetupEndTime ? end >= this.latestSetupEndTime : true);

  private isSubmitDisabled() {
    if (!this.isDisplayChart && this.chartMode === ChartMode.SplitDowntime) {
      this.submitDisabled = true;
    }
  }

  public onDisplayWindowChanged(displayWindow: DisplayWindow): void {
    if (this.displayWindow?.start === displayWindow.start &&
        this.displayWindow?.end === displayWindow.end) {
      return;
    }

    this.displayWindow = displayWindow;

    // reload the setup quantity data for the new display window
    this.loadSetupQuantityDataAsync();
  }

  private EnsureProductionOrderId(): void {
    if (!this.productionOrderId || this.productionOrderId === 0) {
      throw new Error('Production Order Id is required for Edit Setup mode');
    }
  }

  private EnsureRunId(): void {
    if (!this.runId || this.runId === 0) {
      throw new Error('Run Id is required for Edit Setup mode');
    }
  }
}

  class Logger {
    private logs: string = '';

    public clear(): void {
      this.logs = '';
    }

    public append(message: string): void {
      this.logs += message;
    }

    public getLogs(): string {
      return this.logs;
    }
  }
